import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Globe, AlertCircle, FileText, X, Bell, Upload, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getProForms, supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

interface ProForm {
  id: string;
  route_path: string;
  form_name: string;
  form_description: string;
  request_type: string;
  app_id: string;
  hy_user_id: string;
  created_at: string;
  updated_at: string;
  credit_cost: number;
}

interface Request {
  id: string;
  hy_user_id: string;
  request_type: string;
  prompt: string;
  input_media_url: string;
  status: string;
  staff_notes: string;
  output_asset_id: string;
  notification_sent: boolean;
  action_details: any;
  created_at: string;
  updated_at: string;
}

export const ProFormsPage = () => {
  const navigate = useNavigate();
  const [proForms, setProForms] = useState<ProForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingForm, setDeletingForm] = useState<string | null>(null);
  const [accessRecord, setAccessRecord] = useState<any>(null);
  const [showRequestsModal, setShowRequestsModal] = useState<ProForm | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [notifying, setNotifying] = useState<string | null>(null);

  useEffect(() => {
    loadProForms();
    loadAccessRecord();
  }, []);

  const loadAccessRecord = () => {
    setAccessRecord(parseAdminStaffSession());
  };

  const loadProForms = async () => {
    try {
      setLoading(true);
      setError(null);

      const { forms, error: fetchError } = await getProForms();

      if (fetchError) {
        throw new Error(fetchError);
      }

      // Filter forms based on user permissions if not superadmin
      if (accessRecord && accessRecord.role !== 'superadmin') {
        setProForms((forms || []).filter(form => form.hy_user_id === accessRecord.hy_user_id));
      } else {
        setProForms(forms || []);
      }
    } catch (err) {
      console.error('Error loading pro forms:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pro forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/admin/create-pro-form');
  };

  const handleEditProForm = (formId: string) => {
    // Check permissions
    if (accessRecord && accessRecord.role !== 'superadmin') {
      const form = proForms.find(f => f.id === formId);
      if (form && form.hy_user_id !== accessRecord.hy_user_id) {
        alert('Access denied. You can only modify your own pro forms.');
        return;
      }
    }
    navigate(`/admin/create-pro-form?edit=${formId}`);
  };

  const handleDeleteProForm = async (formId: string) => {
    // Check permissions
    if (accessRecord && accessRecord.role !== 'superadmin') {
      const form = proForms.find(f => f.id === formId);
      if (form && form.hy_user_id !== accessRecord.hy_user_id) {
        alert('Access denied. You can only delete your own pro forms.');
        return;
      }
    }

    if (!confirm('Are you sure you want to delete this pro form? This action cannot be undone.')) {
      return;
    }

    setDeletingForm(formId);
    try {
      const { error: deleteError } = await supabase
        .from('pro_forms')
        .delete()
        .eq('id', formId);

      if (deleteError) {
        throw deleteError;
      }
      await loadProForms();
    } catch (err) {
      console.error('Error deleting pro form:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete pro form');
    } finally {
      setDeletingForm(null);
    }
  };

  const handleViewRequests = async (form: ProForm) => {
    setShowRequestsModal(form);
    setRequestsLoading(true);
    try {
      const { data: requestsData, error: requestsError } = await supabase
        .from('requests')
        .select('*')
        .eq('request_type', form.request_type)
        .order('created_at', { ascending: false });

      if (requestsError) {
        throw requestsError;
      }

      setRequests(requestsData || []);
    } catch (err) {
      console.error('Error loading requests:', err);
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleUploadFileForUser = async (request: Request, file: File) => {
    setUploadingFile(request.id);
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('hy_user_id', request.hy_user_id);
      formData.append('isaigenerated', 'true');
      formData.append('filename', file.name);
      
      // Determine media type
      let mediaType = 'image';
      if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('audio/')) mediaType = 'audio';
      
      formData.append('media_type', mediaType);
      
      // Upload file to storage (this would need to be implemented based on your storage system)
      // For now, we'll create a placeholder asset record
      const { data: assetData, error: assetError } = await supabase
        .from('assets')
        .insert({
          hy_user_id: request.hy_user_id,
          public_url: URL.createObjectURL(file), // This should be replaced with actual upload
          isaigenerated: true,
          filename: file.name,
          media_type: mediaType,
          file_size: file.size,
          status: 'completed'
        })
        .select()
        .single();

      if (assetError) throw assetError;

      // Update request status
      await supabase
        .from('requests')
        .update({
          status: 'completed',
          output_asset_id: assetData.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      // Refresh requests
      if (showRequestsModal) {
        await handleViewRequests(showRequestsModal);
      }

      alert('File uploaded successfully and request marked as completed!');
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleSendNotification = async (request: Request) => {
    setNotifying(request.id);
    try {
      // Placeholder for Firebase notification
      // Replace this with your actual Firebase notification logic
      console.log('Sending notification to user:', request.hy_user_id);
      
      // Update notification status in database
      await supabase
        .from('requests')
        .update({
          notification_sent: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      // Refresh requests
      if (showRequestsModal) {
        await handleViewRequests(showRequestsModal);
      }

      alert('Notification sent successfully!');
    } catch (err) {
      console.error('Error sending notification:', err);
      alert('Failed to send notification. Please try again.');
    } finally {
      setNotifying(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-status-warning/20 text-status-warning border-status-warning/30';
      case 'processing':
        return 'bg-accent/20 text-accent border-accent/30';
      case 'completed':
        return 'bg-status-success/20 text-status-success border-status-success/30';
      case 'rejected':
        return 'bg-status-error/20 text-status-error border-status-error/30';
      default:
        return 'bg-textSecondary/20 text-th-secondary border-textSecondary/30';
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar title="Pro Forms Management" subtitle="Manage dynamic pro request forms">
        <button
          type="button"
          onClick={handleCreateNew}
          className="flex items-center space-x-2 px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand-hover transition-all"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Pro Form</span>
        </button>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 pb-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary">Loading pro forms...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4 max-w-md">
                <AlertCircle className="w-16 h-16 text-status-error mx-auto" />
                <h3 className="text-xl font-bold text-th-primary">Failed to Load Pro Forms</h3>
                <p className="text-th-secondary">{error}</p>
                <button onClick={loadProForms} className="button-primary">
                  Try Again
                </button>
              </div>
            </div>
          ) : proForms.length === 0 ? (
            <div className="text-center py-12">
              <div className="space-y-6">
                <div className="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto">
                  <Globe className="w-12 h-12 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-th-primary mb-2">No Pro Forms Yet</h3>
                  <p className="text-th-secondary">
                    Create your first dynamic pro request form to get started
                  </p>
                </div>
                <button onClick={handleCreateNew} className="button-primary">
                  Create New Pro Form
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {proForms.map((form) => (
                <div key={form.id} className="card p-4 hover:bg-overlay transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-th-primary truncate">{form.form_name}</h3>
                      <p className="text-xs text-th-secondary truncate">{form.form_description}</p>
                      <p className="text-xs text-th-secondary font-mono">Route: /pro{form.route_path}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-xs text-th-secondary">Request Type: {form.request_type}</p>
                        <p className="text-xs text-th-secondary">Cost: {form.credit_cost} credits</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-4">
                      {/* View Requests */}
                      <button
                        onClick={() => handleViewRequests(form)}
                        className="flex items-center space-x-1 px-2 py-1.5 bg-accent/20 border border-accent/30 text-accent hover:bg-accent/30 rounded text-xs font-medium transition-all"
                        title="View User Requests"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        <span className="hidden lg:inline">Requests</span>
                      </button>
                      
                      <button
                        onClick={() => handleEditProForm(form.id)}
                        className="flex items-center space-x-1 px-2 py-1.5 bg-status-warning/20 border border-status-warning/30 text-status-warning hover:bg-status-warning/30 rounded text-xs font-medium transition-all"
                        title="Edit Pro Form"
                      >
                        <Edit className="w-3 h-3" />
                        <span className="hidden lg:inline">Edit</span>
                      </button>
                      
                      <button
                        onClick={() => handleDeleteProForm(form.id)}
                        disabled={deletingForm === form.id}
                        className="flex items-center space-x-1 px-2 py-1.5 bg-status-error/20 border border-status-error/30 text-status-error hover:bg-status-error/30 rounded text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete Pro Form"
                      >
                        {deletingForm === form.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        <span className="hidden lg:inline">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Requests Modal */}
      {showRequestsModal && (
        <div className="fixed inset-0 bg-surface z-modal-content flex flex-col">
          <div className="w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-subtle bg-surface flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-secondary to-primary rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-th-primary">User Requests</h2>
                  <p className="text-sm text-th-secondary">
                    {showRequestsModal.form_name} - {requests.length} request{requests.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRequestsModal(null)}
                className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {requestsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center space-y-4">
                    <LoadingSpinner size="lg" />
                    <p className="text-th-secondary">Loading requests...</p>
                  </div>
                </div>
              ) : requests.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center space-y-4">
                    <FileText className="w-16 h-16 text-th-secondary mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-th-primary mb-2">No Requests Yet</h3>
                    <p className="text-th-secondary">
                      No users have submitted requests for this pro form yet.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-surface border-b border-subtle sticky top-0">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Request ID</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">User ID</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Prompt</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Media</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Created</th>
                        <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider w-40">Actions</th>
                      </tr>
                    </thead>
                    
                    <tbody className="divide-y divide-hyperBorder">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-overlay transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-th-primary font-medium font-mono text-sm">
                                #{request.id.slice(0, 8)}
                              </span>
                              {request.notification_sent && (
                                <span className="text-xs px-1.5 py-0.5 bg-brand/20 text-brand rounded border border-brand/30">
                                  ✓
                                </span>
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3">
                            <span className="text-th-primary font-mono text-xs">
                              {request.hy_user_id.slice(0, 12)}...
                            </span>
                          </td>
                          
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium border ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          </td>
                          
                          <td className="px-4 py-3 max-w-xs">
                            {request.prompt ? (
                              <p className="text-th-primary text-sm truncate" title={request.prompt}>
                                {request.prompt}
                              </p>
                            ) : (
                              <span className="text-th-secondary text-sm italic">No prompt</span>
                            )}
                          </td>
                          
                          <td className="px-4 py-3">
                            {request.input_media_url ? (
                              <a 
                                href={request.input_media_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-accent text-xs underline hover:text-accent/80"
                              >
                                View File
                              </a>
                            ) : (
                              <span className="text-th-secondary text-xs">-</span>
                            )}
                          </td>
                          
                          <td className="px-4 py-3">
                            <span className="text-th-secondary text-xs">
                              {new Date(request.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-1">
                              <label className={`flex items-center px-2 py-1 bg-brand text-surface rounded text-xs font-medium hover:bg-brand-hover transition-all cursor-pointer ${uploadingFile === request.id ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <input
                                  type="file"
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      handleUploadFileForUser(request, e.target.files[0]);
                                    }
                                  }}
                                  className="hidden"
                                  accept="image/*,video/*,audio/*"
                                  disabled={uploadingFile === request.id}
                                />
                                {uploadingFile === request.id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <Upload className="w-3 h-3" />
                                )}
                              </label>
                              
                              <button
                                onClick={() => handleSendNotification(request)}
                                disabled={notifying === request.id}
                                className="flex items-center px-2 py-1 bg-status-warning text-black rounded text-xs font-medium hover:bg-status-warning/90 transition-all disabled:opacity-50"
                                title="Send Notification"
                              >
                                {notifying === request.id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <Bell className="w-3 h-3" />
                                )}
                              </button>
                              
                              {request.output_asset_id && (
                                <button
                                  onClick={async () => {
                                    try {
                                      const { data: asset } = await supabase
                                        .from('assets')
                                        .select('public_url, filename')
                                        .eq('id', request.output_asset_id)
                                        .single();
                                      
                                      if (asset) {
                                        const link = document.createElement('a');
                                        link.href = asset.public_url;
                                        link.download = asset.filename || `request-${request.id}`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    } catch (err) {
                                      alert('Download failed');
                                    }
                                  }}
                                  className="flex items-center px-2 py-1 bg-status-warning text-white rounded text-xs font-medium hover:bg-status-warning/90 transition-all"
                                  title="Download Asset"
                                >
                                  <Download className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-subtle bg-surface flex-shrink-0">
              <div className="text-sm text-th-secondary">
                {requests.length > 0 ? (
                  `${requests.length} request${requests.length !== 1 ? 's' : ''} • ${requests.filter(r => r.status === 'pending').length} pending`
                ) : (
                  'No requests found for this pro form'
                )}
              </div>
              <button
                onClick={() => setShowRequestsModal(null)}
                className="px-4 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary hover:border-brand/50 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};